import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InternalApiScreeningAlertStatus } from '../../api-internal';
import { uniqBy } from 'lodash';

export interface ScreeningAlertStatusesState {
  statuses: InternalApiScreeningAlertStatus[];
  activeStatuses: InternalApiScreeningAlertStatus[];
  deactivatedUniqueStatuses: InternalApiScreeningAlertStatus[];
  hasFetchedStatuses: boolean;
}

const initialState: ScreeningAlertStatusesState = {
  statuses: [],
  activeStatuses: [],
  deactivatedUniqueStatuses: [],
  hasFetchedStatuses: false,
};

const screeningAlertStatusesSlice = createSlice({
  name: 'screening-alert-statuses',
  initialState,
  reducers: {
    fetchAlertStatusesSuccess: (state, action: PayloadAction<Array<InternalApiScreeningAlertStatus>>) => {
      const currentTime = new Date();
      const newStatuses = action.payload;
      const { activeStatuses, deactivatedStatuses } = partitionStatuses(newStatuses, currentTime);
      return {
        ...state,
        statuses: newStatuses,
        activeStatuses: activeStatuses,
        deactivatedUniqueStatuses: deactivatedStatuses,
        hasFetchedStatuses: true,
      };
    },
    fetchActiveUserAlertStatusesSuccess: (state, action: PayloadAction<Array<InternalApiScreeningAlertStatus>>) => {
      const currentTime = new Date();
      const activeUserStatuses = action.payload;
      const activeIds = activeUserStatuses.map((status) => status.id!);
      const newStatuses = state.statuses.filter((status) => !activeIds.includes(status.id!)).concat(activeUserStatuses);

      const { activeStatuses, deactivatedStatuses } = partitionStatuses(newStatuses, currentTime);

      return {
        ...state,
        statuses: newStatuses,
        activeStatuses: activeStatuses,
        deactivatedUniqueStatuses: deactivatedStatuses,
        hasFetchedStatuses: true,
      };
    },
  },
});

const partitionStatuses = (statuses: InternalApiScreeningAlertStatus[], currentTime: Date) => {
  const activeStatuses = statuses.filter(
    (it) => it.startTime && it.startTime <= currentTime && (!it.endTime || it.endTime >= currentTime)
  );
  const activeStatusCodes = activeStatuses.map((status) => status.code);
  const deactivatedStatuses = uniqBy(
    statuses
      .filter((status) => !activeStatusCodes.includes(status.code))
      .sort((a, b) => b.endTime!.getTime() - a.endTime!.getTime()),
    (status) => status.code
  );
  return { activeStatuses, deactivatedStatuses };
};

// Extract the action creators object and the reducer
const { actions, reducer } = screeningAlertStatusesSlice;
// Extract and export each action creator by name
export const { fetchAlertStatusesSuccess, fetchActiveUserAlertStatusesSuccess } = actions;
// Export the reducer, either as a default or named export
export default reducer;
